"use client";

import { JSX, Fragment } from "react";
import { type LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import type { ModuleDeltaComparisonData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import { type Sentiment } from "./shared/comparison";

interface DeltaValue {
  value: string;
  label?: string;
  highlighted?: boolean;
}

interface DeltaMetric {
  id: string | number;
  label: string;
  values: DeltaValue[];
  delta?: number;
  sentiment?: Sentiment;
}

function trendMeta(sentiment: Sentiment | undefined, delta: number, grayscale?: boolean): {
  Icon: LucideIcon;
  name: string;
  color: string;
} {
  const direction: "up" | "down" | "flat" =
    delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const Icon: LucideIcon =
    direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const color = grayscale
    ? sentiment === "good" || sentiment === "bad"
      ? "var(--fg)"
      : "var(--subtle)"
    : sentiment === "good"
      ? "var(--emerald)"
      : sentiment === "bad"
        ? "var(--rose)"
        : "var(--subtle)";
  const name =
    direction === "up" ? "trending-up" : direction === "down" ? "trending-down" : "minus";
  return { Icon, name, color };
}

export default function ModuleDeltaComparison({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleDeltaComparisonData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { intlLocale } = useViewerLanguage();
  const metrics = data.metrics as DeltaMetric[];

  const row = useHoverSelect<string | number>();
  const effective = row.effective;
  const hasHover = useHoverCapable();

  return (
    <div className={cn("w-full", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll className="pb-2 [mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)]">
        <div className="grid grid-rows-3 grid-flow-col auto-cols-[minmax(220px,auto)] w-max pr-10 border-t border-l border-dashed border-[var(--border-dashed)]">
          {metrics.map((metric, i) => {
            const isHovered = effective === metric.id;
            const dim = effective !== null && !isHovered;

            const meta =
              metric.delta !== undefined
                ? trendMeta(metric.sentiment, metric.delta, grayscale)
                : null;

            return (
              <button
                key={metric.id}
                type="button"
                aria-pressed={row.selected === metric.id}
                className="w-full py-2 px-3 text-left cursor-pointer border-r border-b border-dashed border-[var(--border-dashed)]"
                style={{
                  opacity: dim ? 0.4 : 1,
                  transition: "opacity 0.15s, background 0.15s",
                }}
                onMouseEnter={hasHover ? () => row.setHovered(metric.id) : undefined}
                onMouseLeave={hasHover ? () => row.setHovered(null) : undefined}
                onFocus={hasHover ? () => row.setHovered(metric.id) : undefined}
                onBlur={hasHover ? () => row.setHovered(null) : undefined}
                onClick={(e) =>
                  row.select(metric.id, {
                    kind: "row",
                    id: metric.id,
                    index: i,
                    element: e.currentTarget,
                  })
                }
              >
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <span
                    className={cn(
                      "text-[11px] font-medium text-[var(--muted)] leading-tight",
                      MODULE_LABEL_WRAP.cell,
                    )}
                  >
                    {stripInlineMarkdown(metric.label)}
                  </span>
                  {meta && metric.delta !== undefined && (
                    <span
                      className="flex items-center gap-0.5 text-[11px] tabular-nums font-medium shrink-0"
                      style={{ color: meta.color }}
                    >
                      <AnimatedIcon name={meta.name} fallback={meta.Icon} size={10} strokeWidth={2} />
                      {metric.delta > 0 ? "+" : ""}
                      {fmtNum(metric.delta, 1, intlLocale)}%
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-1.5 flex-wrap">
                  {metric.values.map((v, i) => (
                    <Fragment key={i}>
                      <span
                        className={cn(
                          "tabular-nums transition-all",
                          v.highlighted
                            ? "text-[var(--fg)] text-[15px] font-medium"
                            : "text-[var(--subtle)] text-[12px] font-normal",
                        )}
                      >
                        {v.value}
                      </span>
                      {i < metric.values.length - 1 && (
                        <span className="text-[var(--subtle)] text-[10px]">→</span>
                      )}
                    </Fragment>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </SwipeScroll>
    </div>
  );
}
